import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { HandHelping, Check, X, Edit3, AlertCircle, Loader2, MessageSquarePlus } from 'lucide-react';
import { toast } from 'sonner';
import SendDirectMessageDialog from './SendDirectMessageDialog';

/**
 * AdminPendingPrayers
 * Combined queue: shows both new prayer submissions awaiting approval AND
 * approved prayers with a staged edit awaiting review. Admin can approve/reject
 * each.
 */
export const AdminPendingPrayers = () => {
  const { API } = useContext(AuthContext);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [dmOpen, setDmOpen] = useState(false);
  const [dmTarget, setDmTarget] = useState({ recipient: null, prayerId: null, subject: '' });

  const fetchPending = async () => {
    try {
      const res = await axios.get(`${API}/admin/prayers/pending`);
      setPending(res.data || []);
    } catch (e) {
      toast.error('Failed to load pending prayers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
    // eslint-disable-next-line
  }, []);

  const act = async (label, fn, prayerId) => {
    setActingId(prayerId);
    try {
      await fn();
      toast.success(label);
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${label.toLowerCase()}`);
    } finally {
      setActingId(null);
    }
  };

  const approveNew = (p) =>
    act('Prayer approved', () => axios.post(`${API}/admin/prayers/${p.id}/approve`), p.id);
  const rejectNew = (p) =>
    act('Prayer rejected', () => axios.post(`${API}/admin/prayers/${p.id}/reject`), p.id);
  const approveEdit = (p) =>
    act('Edit approved', () => axios.post(`${API}/admin/prayers/${p.id}/approve-edit`), p.id);
  const rejectEdit = (p) =>
    act('Edit discarded', () => axios.post(`${API}/admin/prayers/${p.id}/reject-edit`), p.id);

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    } catch (e) {
      return iso;
    }
  };

  const newSubmissions = pending.filter((p) => !p.approved);
  const editRequests = pending.filter((p) => p.approved && p.edit_pending);

  return (
    <Card className="mt-8" data-testid="admin-pending-prayers">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-2xl font-heading text-[#2B6B7F] flex items-center gap-2">
              <HandHelping className="w-6 h-6" />
              Prayer Approval Queue
            </CardTitle>
            <CardDescription>
              Review new prayer submissions and edits to existing prayers before they go public.
            </CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Badge className="bg-amber-500" data-testid="pending-new-count">
              {newSubmissions.length} new
            </Badge>
            <Badge className="bg-blue-500" data-testid="pending-edit-count">
              {editRequests.length} edit{editRequests.length === 1 ? '' : 's'}
            </Badge>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchPending}
              disabled={loading}
              data-testid="refresh-pending-prayers"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading && pending.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
        ) : pending.length === 0 ? (
          <div className="text-center py-8" data-testid="no-pending-empty">
            <HandHelping className="w-12 h-12 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Nothing waiting on you. Great work!</p>
          </div>
        ) : (
          <>
            {/* New submissions */}
            {newSubmissions.length > 0 && (
              <div className="space-y-3" data-testid="new-submissions-section">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  New prayer submissions
                </h4>
                {newSubmissions.map((p) => (
                  <div
                    key={p.id}
                    className="border border-amber-200 bg-amber-50/40 rounded-lg p-4"
                    data-testid={`pending-prayer-${p.id}`}
                  >
                    <div className="flex justify-between items-start mb-2 gap-3 flex-wrap">
                      <div>
                        <p className="font-medium text-slate-800">{p.user_name}</p>
                        <p className="text-xs text-slate-500">
                          {fmtDate(p.created_at)} • {p.visibility} • {p.prayer_type}
                          {p.source === 'email' && (
                            <Badge variant="outline" className="ml-2 text-[10px]">Email</Badge>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => approveNew(p)}
                          disabled={actingId === p.id}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`approve-prayer-${p.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => rejectNew(p)}
                          disabled={actingId === p.id}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          data-testid={`reject-prayer-${p.id}`}
                        >
                          <X className="w-4 h-4 mr-1" /> Reject
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setDmTarget({
                              recipient: { id: p.user_id, name: p.user_name },
                              prayerId: p.id,
                              subject: 'About your prayer request',
                            });
                            setDmOpen(true);
                          }}
                          className="border-[#2B6B7F] text-[#2B6B7F] hover:bg-[#2B6B7F] hover:text-white"
                          data-testid={`message-author-${p.id}`}
                        >
                          <MessageSquarePlus className="w-4 h-4 mr-1" /> Message author
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {p.prayer_text}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Edit requests */}
            {editRequests.length > 0 && (
              <div className="space-y-3" data-testid="edit-requests-section">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Edit3 className="w-4 h-4 text-blue-500" />
                  Edit requests on approved prayers
                </h4>
                {editRequests.map((p) => (
                  <div
                    key={p.id}
                    className="border border-blue-200 bg-blue-50/40 rounded-lg p-4"
                    data-testid={`pending-edit-${p.id}`}
                  >
                    <div className="flex justify-between items-start mb-2 gap-3 flex-wrap">
                      <div>
                        <p className="font-medium text-slate-800">{p.user_name}</p>
                        <p className="text-xs text-slate-500">
                          Originally posted {fmtDate(p.created_at)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => approveEdit(p)}
                          disabled={actingId === p.id}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`approve-edit-${p.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => rejectEdit(p)}
                          disabled={actingId === p.id}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          data-testid={`reject-edit-${p.id}`}
                        >
                          <X className="w-4 h-4 mr-1" /> Discard
                        </Button>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3 mt-3">
                      <div className="bg-white border border-slate-200 rounded-md p-3">
                        <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Current (live)</p>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{p.prayer_text}</p>
                      </div>
                      <div className="bg-white border border-blue-300 rounded-md p-3">
                        <p className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold mb-1">Proposed edit</p>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{p.pending_text}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      <SendDirectMessageDialog
        open={dmOpen}
        onOpenChange={setDmOpen}
        recipient={dmTarget.recipient}
        prayerId={dmTarget.prayerId}
        defaultSubject={dmTarget.subject}
      />
    </Card>
  );
};

export default AdminPendingPrayers;
